import React from "react";
import { Col, Collapse, Row } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { MONTHLY_CHARGE_KEYS, money, rentBreakdownFromItem, totalRentFromItem } from "../PadInfo/padInfoTypes";

// Visual tokens — the PAD modal's, so the tenant form reads as one product.
const C = {
  primary: "#0C447C",
  primaryMd: "#1A6FB5",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  soft: "#F7F9FC",
};

const CHARGE_KEYS = new Set(MONTHLY_CHARGE_KEYS.flatMap((k) => [k.key, ...(k.aliases || [])]));

const isChargeField = (f) => CHARGE_KEYS.has(f.key) || f.key === "targetRent" || /fee$/i.test(f.key);
const isLeaseField = (f) => f.type === "date" || /lease|deposit|^sd$|movein|moveout/i.test(f.key);
const isResidentField = (f) => /tenant|occupant|resident|phone|email|contact|name/i.test(f.key);

/**
 * Assign the property's dynamic field layout into display clusters. Order within a cluster
 * is the layout's own; a field matching nothing lands in Other, so no layout loses a field.
 * Exported for tests.
 */
export function clusterFields(fields) {
  const clusters = [
    { title: "Resident", icon: "user", fields: [] },
    { title: "Lease & dates", icon: "calendar", fields: [] },
    { title: "Monthly charges", icon: "dollar-sign", fields: [] },
    { title: "Other", icon: "grid", fields: [] },
  ];
  for (const f of fields || []) {
    if (isChargeField(f)) clusters[2].fields.push(f);
    else if (isLeaseField(f)) clusters[1].fields.push(f);
    else if (isResidentField(f)) clusters[0].fields.push(f);
    else clusters[3].fields.push(f);
  }
  return clusters;
}

/**
 * The Tenant Basic Information card: the dynamic layout's fields, clustered and restyled,
 * with a read-only Total Rent tile heading the Monthly charges cluster. Field rendering
 * stays the caller's (DynamicField, passed in as `renderField`) — this component only
 * arranges and dresses it.
 */
const BasicInfoSection = ({ fields, values, open, onToggle, hasErrors, renderField }) => {
  const clusters = clusterFields(fields).filter((c) => c.fields.length);
  // Live from the form values, over the same keys the mastersheet's Total Rent column and
  // the PAD module sum — the three always agree. Nothing is stored.
  const totalRent = totalRentFromItem(values || {});
  const breakdown = rentBreakdownFromItem(values || {});

  return (
    <div className="tbi-card">
      <style>{`
        .tbi-card { border: 1px solid ${C.border}; border-radius: 14px; background: #fff; margin-bottom: 1.5rem; overflow: hidden; box-shadow: 0 2px 10px rgba(12,68,124,0.05); }
        .tbi-head { display: flex; align-items: center; gap: 11px; width: 100%; padding: 12px 18px; border: none; background: #fff; cursor: pointer; font-family: inherit; text-align: left; }
        .tbi-head:hover { background: ${C.soft}; }
        .tbi-head .ic { width: 34px; height: 34px; border-radius: 9px; background: #EBF3FC; color: ${C.primary}; display: inline-flex; align-items: center; justify-content: center; flex: none; }
        .tbi-head .t { font-size: 16px; font-weight: 700; color: ${C.text}; line-height: 1.2; }
        .tbi-head .n { display: block; font-size: 11px; color: ${C.muted}; margin-top: 2px; }
        .tbi-head .err { color: #A32D2D; font-size: 11px; font-weight: 700; margin-left: auto; display: inline-flex; align-items: center; gap: 5px; flex: none; }
        .tbi-head .chev { margin-left: auto; color: ${C.muted}; flex: none; }
        .tbi-head .err + .chev { margin-left: 10px; }
        .tbi-body { padding: 4px 18px 18px; }
        .tbi-cluster { margin-top: 14px; }
        .tbi-cluster-title { display: flex; align-items: center; gap: 7px; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: ${C.muted}; border-bottom: 1px solid ${C.border}; padding-bottom: 6px; margin-bottom: 12px; }
        .tbi-tile { display: flex; align-items: center; gap: 10px; border: 1px solid ${C.inputBorder}; border-radius: 10px; background: ${C.soft}; padding: 10px 14px; margin-bottom: 14px; max-width: 420px; }
        .tbi-tile .lbl { font-size: 12px; font-weight: 600; color: ${C.muted}; display: inline-flex; align-items: center; gap: 6px; }
        .tbi-tile .val { font-size: 20px; font-weight: 800; color: ${C.text}; margin-left: auto; }
        .tbi-tile .lock { color: ${C.muted}; flex: none; }
        /* The dynamic fields, dressed in the PAD modal's language without touching them. */
        .tbi-card label { font-size: 12px; font-weight: 600; color: ${C.text}; margin-bottom: 5px; }
        .tbi-card .form-control, .tbi-card .form-select { min-height: 38px; font-size: 13px; color: ${C.text}; border: 1px solid ${C.inputBorder}; border-radius: 9px; }
        .tbi-card .form-control:focus, .tbi-card .form-select:focus { border-color: ${C.primaryMd}; box-shadow: 0 0 0 2px rgba(26,111,181,0.12); }
        .tbi-card .form-control:disabled, .tbi-card .form-control[readonly] { background: ${C.soft}; color: ${C.muted}; }
        .tbi-card .form-text { font-size: 11px; color: ${C.muted}; }
        .tbi-card .invalid-feedback { font-size: 11px; }
      `}</style>

      <button type="button" className="tbi-head" onClick={onToggle} aria-expanded={!!open}>
        <span className="ic"><FeatherIcon icon="user" size={17} /></span>
        <span>
          <span className="t">Tenant Basic Information</span>
          <span className="n">{(fields || []).length} fields · Total Rent {totalRent === null ? "—" : money(totalRent)}</span>
        </span>
        {hasErrors && (
          <span className="err"><FeatherIcon icon="alert-circle" size={13} /> Needs attention</span>
        )}
        <span className="chev"><FeatherIcon icon={open ? "chevron-up" : "chevron-down"} size={17} /></span>
      </button>

      <Collapse isOpen={!!open}>
        <div className="tbi-body">
          {clusters.map((cluster) => (
            <div className="tbi-cluster" key={cluster.title}>
              <div className="tbi-cluster-title">
                <FeatherIcon icon={cluster.icon} size={13} />
                {cluster.title}
              </div>
              {cluster.title === "Monthly charges" && (
                <div
                  className="tbi-tile"
                  title={breakdown.length ? breakdown.map((r) => `${r.label} ${money(r.amount)}`).join(" + ") : "No monthly charges entered yet"}
                >
                  <span className="lbl"><FeatherIcon icon="dollar-sign" size={14} /> Total Rent</span>
                  <span className="val">{totalRent === null ? "—" : money(totalRent)}</span>
                  <FeatherIcon className="lock" icon="lock" size={13} />
                </div>
              )}
              <Row>
                {cluster.fields.map((field) => (
                  <Col md={3} key={field.key}>
                    {renderField(field)}
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
};

export default BasicInfoSection;
